import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { Spinner } from 'react-bootstrap';
import { FaWind } from 'react-icons/fa';
import CommentForm from './CommentForm';
import CommentItem from './CommentItem';

const ITEMS_PER_PAGE = 12;

const CommentSection = ({ postId }) => {
    const [comments, setComments] = useState([]);
    const [, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const observer = useRef();

    // 댓글 조회 함수
    const fetchComments = useCallback(async (pageNum) => {
        if (loading) return;
        setLoading(true);
        try {
            const response = await axiosInstance.get(`comments?post_id=${postId}&page=${pageNum}`);
            const apiData = response.data.data;

            if (apiData?.postList && apiData.postList.length > 0) {
                setComments(prev => {
                    // 중복 제거 (ID 기준)
                    const newComments = apiData.postList.filter(c => !prev.some(existing => existing.json.id === c.json.id));
                    return [...prev, ...newComments];
                });

                // 더 이상 불러올 데이터가 없는지 확인 (간단히 개수로 체크)
                if (apiData.postList.length < ITEMS_PER_PAGE) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 초기 로딩
    useEffect(() => {
        setComments([]);
        setPage(1);
        setHasMore(true);
        fetchComments(1);
    }, [postId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 무한 스크롤 옵저버
    const lastCommentElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => {
                    const nextPage = prevPage + 1;
                    fetchComments(nextPage);
                    return nextPage;
                });
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchComments]);

    // 댓글 작성/답글 작성 성공 시 리스트 새로고침 (단순화를 위해 1페이지부터 다시 로드)
    const handleRefresh = () => {
        setComments([]);
        setPage(1);
        setHasMore(true);
        fetchComments(1);
    };

    // 평탄한 리스트를 트리 구조로 변환
    const buildCommentTree = (flatComments) => {
        const commentMap = {};
        const roots = [];

        // 1. 모든 댓글을 맵에 저장하고 children 배열 초기화
        flatComments.forEach(c => {
            commentMap[c.json.id] = { ...c, children: [] };
        });

        // 2. 부모-자식 관계 연결
        flatComments.forEach(c => {
            const comment = commentMap[c.json.id];
            if (c.json.parent_id && commentMap[c.json.parent_id]) {
                commentMap[c.json.parent_id].children.push(comment);
            } else {
                roots.push(comment);
            }
        });

        // 3. 최신순/등록순 정렬 (필요 시)
        // roots.sort((a, b) => new Date(b.json.created_at) - new Date(a.json.created_at));

        return roots;
    };

    const commentTree = buildCommentTree(comments);

    return (
        <div className="comment-section mt-5 pt-4 border-top">
            <h4 className="mb-4 fw-bold">반응 <span className="text-primary">{comments.length}</span></h4>

            {/* 댓글 작성 폼 (최상위) */}
            <div className="mb-5">
                <CommentForm postId={postId} onSuccess={handleRefresh} />
            </div>

            {/* 댓글 목록 */}
            <div className="comment-list">
                {commentTree.map((comment, index) => {
                    const isLastElement = index === commentTree.length - 1;
                    return (
                        <div key={comment.json.id} ref={isLastElement ? lastCommentElementRef : null}>
                            <CommentItem
                                comment={comment}
                                childrenComments={comment.children}
                                onReplySuccess={handleRefresh}
                            />
                        </div>
                    );
                })}
            </div>

            {loading && (
                <div className="text-center my-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                </div>
            )}

            {!loading && comments.length === 0 && (
                <div className="text-center my-5 empty-message">
                    <style>
                        {`
                            @import url('https://fonts.googleapis.com/css2?family=Gamja+Flower&display=swap');
                            .empty-message {
                                font-family: 'Gamja Flower', cursive;
                                font-size: 1.5rem;
                                color: #78909c; /* Cool Blue Gray */
                                display: flex;
                                flex-direction: column;
                                align-items: center;
                                gap: 10px;
                            }
                            .empty-icon {
                                font-size: 2.5rem;
                                color: #b0bec5;
                                animation: breeze 3s infinite ease-in-out;
                            }
                            @keyframes breeze {
                                0% { transform: translateX(0); opacity: 0.7; }
                                50% { transform: translateX(10px); opacity: 1; }
                                100% { transform: translateX(0); opacity: 0.7; }
                            }
                        `}
                    </style>
                    <div className="empty-icon"><FaWind /></div>
                    <p>아직 반응이.. 싸늘하다..</p>
                </div>
            )}
        </div>
    );
};

export default CommentSection;
